import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import StructuredData from "@/components/StructuredData";
import SEOHead from "@/components/SEOHead";
import FloatingActions from "@/components/FloatingActions";
import QuickActions from "@/components/QuickActions";
import { Palette, Heart, Star, Zap, Users, Trophy, Camera, Eye, Award, Phone, Mail } from "lucide-react";
import { trackSocialClick } from "@/lib/analytics";
import valeriaPhoto1 from "@/assets/valeria-wagner-1.png";
import valeriaPhoto2 from "@/assets/valeria-wagner-2.png";
import valeriaPhoto3 from "@/assets/valeria-wagner-3.png";

const About = () => {
  const stats = [
    { icon: Star, number: "5", label: "Лет опыта" },
    { icon: Users, number: "20+", label: "Довольных клиентов" },
    { icon: Palette, number: "150+", label: "Выполненных работ" },
    { icon: Trophy, number: "25+", label: "Выставок и проектов" },
  ];

  const services = [
    {
      icon: Palette,
      title: "Картины на заказ",
      description: "Пейзажи, абстракции в различных техниках",
      techniques: ["Масло", "Акрил", "Смешанная техника"]
    },
    {
      icon: Heart,
      title: "Роспись стен",
      description: "Художественное оформление интерьеров и экстерьеров",
      techniques: ["Детские комнаты", "Кафе и рестораны", "Офисы"]
    },
    {
      icon: Zap,
      title: "Уличное искусство",
      description: "Роспись стен для интерьера и экстерьера",
      techniques: ["Роспись стен", "Декоративная живопись", "Интерьерная роспись"]
    },
    {
      icon: Eye,
      title: "Арт-консультации",
      description: "Помощь в выборе и размещении произведений",
      techniques: ["Подбор стиля", "Композиция", "Цветовые решения"]
    }
  ];

  return (
    <>
      <SEOHead
        title="О художнике Валерии Вагнер из Омска - Живопись, роспись стен | W-Wagner.art"
        description="Валерия Вагнер - профессиональный художник из Омска с 5 лет опыта. Выпускница художественной школы с отличием. Картины маслом и акрилом, роспись стен, интерьерная живопись. Более 20 довольных клиентов в Омске и области."
        keywords={['Валерия Вагнер художник', 'о художнике Омск', 'биография художника', 'живопись Омск', 'роспись стен Омск', 'художник Омск', 'мастерская художника Омск']}
        canonicalUrl="https://w-wagner.art/about"
      />
      <StructuredData type="about" />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-card via-background to-card/50">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(var(--primary)/0.1),transparent_50%)]" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <div className="text-center space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                  <Palette className="w-4 h-4" />
                  Художник из Омска
                </div>
                <h1 className="text-6xl md:text-8xl font-bold">
                  <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                    О художнике
                  </span>
                </h1>
                <p className="text-2xl md:text-3xl text-muted-foreground font-light max-w-4xl mx-auto">
                  Валерия Вагнер — художник с душой и многолетним опытом создания уникальных произведений искусства в Омске.
                </p>
              </div>
              
              <div className="flex flex-wrap justify-center gap-3">
                {["Живопись маслом", "Роспись стен", "Акриловая живопись", "Пейзажи Омска"].map((tag) => (
                  <Badge 
                    key={tag}
                    variant="outline" 
                    className="text-base px-6 py-3 border-primary/30 hover:border-primary/50 transition-colors"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>


        {/* Artist Bio Section */}
        <section className="py-24 bg-card/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm font-medium">
                    <Heart className="w-3 h-3" />
                    Об Авторе
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold">
                    Валерия 
                    <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent"> Вагнер</span>
                  </h2>
                  <p className="text-xl text-primary font-medium">
                    Художник с душой и многолетним опытом в Омске
                  </p>
                </div>

                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Меня зовут <strong className="text-foreground">Валерия Вагнер</strong>, и на данный момент 
                    я являюсь художником как на холстах, так и на стенах. Картина — это то, 
                    чем я живу уже несколько лет. По этой причине хотелось поделиться ими с вами как Автор.
                  </p>
                  <p>
                    Сколько себя помню, мне всегда нравилось рисовать. Первые мои шаги в живописи 
                    начались, когда мне было около двух лет — я могла часами сидеть и мазюкать 
                    кистью с краской листы бумаги. Это единственное занятие, которое могло увлечь меня надолго.
                  </p>
                  <p>
                    В старших классах я окончила художественную школу с отличием. Моим учителем был 
                    местный известный художник <strong className="text-foreground">Волков Раим Ризович</strong>, 
                    именно он обучал меня основам живописи и рисунка в Омске.
                  </p>
                  <p>
                    После окончания школы я написала свою первую настоящую картину маслом, которую подарила 
                    своей бабушке. Это и стало началом моего пути в мир живописи. 
                    <strong className="text-foreground"> Мои работы можно оценить в моей галерее.</strong>
                  </p>
                </div>

                
                {/* Блок QuickActions */}
                <div className="mt-6">
                  <QuickActions showTitle={true} />
                </div>
              </div>

              <div className="space-y-6">
                <div className="relative">
                  <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-primary/10">
                    <img 
                      src={valeriaPhoto1} 
                      alt="Валерия Вагнер - профессиональный художник из Омска с кистями в руках. Специализируется на живописи маслом, акрилом и росписи стен в Омске" 
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute -inset-6 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl -z-10 blur-2xl opacity-40" />
                </div>


                {/* Removed additional photos as requested */}
              </div>
            </div>
          </div>
        </section>

      {/* Services Section */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 max-w-4xl mx-auto">Что я создаю</h2>
            <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
              Каждый проект — это возможность воплотить уникальную идею в жизнь, 
              будь то личный заказ или масштабное оформление пространства
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/20">
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors flex items-center justify-center">
                        <service.icon className="w-8 h-8 text-primary" />
                      </div>
                    </div>
                    <div className="space-y-4 flex-1">
                      <h3 className="text-2xl font-semibold group-hover:text-primary transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {service.techniques.map((technique, techIndex) => (
                          <Badge 
                            key={techIndex}
                            variant="secondary" 
                            className="text-sm px-3 py-1"
                          >
                            {technique}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10 rounded-3xl" />
            <div className="relative p-12 space-y-8">
              <div className="space-y-4">
                <h2 className="text-4xl font-bold max-w-4xl mx-auto">
                  Готовы создать что-то 
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"> особенное?</span>
                </h2>
                <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
                  Обсудим ваши идеи и воплотим их в уникальное произведение искусства. 
                  Работаю по всему Омску и области.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg" className="gold-gradient text-black font-medium">
                    <Link to="/services">
                      <Phone className="w-5 h-5 mr-2" />
                      Заказать работу
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <Link to="/raboty">Посмотреть портфолио</Link>
                  </Button>
                </div>
                
                <div className="flex items-center justify-center gap-4">
                  <a 
                    href="https://wa.me/79507818785" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => trackSocialClick('WhatsApp', 'страница о художнике')}
                    className="flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 hover:bg-green-500/20 transition-colors group"
                  >
                    <svg className="w-6 h-6 text-green-600 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                    </svg>
                  </a>
                  <a 
                    href="https://t.me/FallenSonS"
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-500/10 hover:bg-blue-500/20 transition-colors group"
                  >
                    <svg className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://vk.com/im?sel=-214021218" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => trackSocialClick('VK Сообщения', 'about_page')}
                    className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-400/10 hover:bg-blue-400/20 transition-colors group"
                  >
                    <svg className="w-6 h-6 text-blue-500 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zM12 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6zm6-10.5c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5 1.5z"/>
                      <circle cx="12" cy="12" r="3" fill="white"/>
                      <text x="12" y="8" textAnchor="middle" fontSize="4" fill="currentColor">VK</text>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <FloatingActions currentPage="about" />
    </div>
    </>
  );
};

export default About;